import { useEffect, useState } from "react";
import {
  X, Play, Square, Sparkles, UploadCloud, Loader2, Plus, Clock,
  Trash2, Wand2, ArrowLeft, Flag,
} from "lucide-react";
import { useActivityCategoryStore } from "../../store/useActivityCategoryStore";
import {
  useActivityTaskStore,
  type ActivityStatus,
} from "../../store/useActivityTaskStore";
import { useClientStore } from "../../store/useClientStore";
import { useProjectStore } from "../../store/useProjectStore";
import { useBidStore } from "../../store/useBidStore";
import { useProposalStore } from "../../store/useProposalStore";
import { fmtDuration, displayElapsed } from "../../lib/activityTime";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const STATUSES: ActivityStatus[] = ["Planned", "In Progress", "Completed", "On Hold"];

// Estimated-time dropdowns — minutes in fixed 5-minute steps.
const HOUR_OPTIONS = Array.from({ length: 13 }, (_, i) => i); // 0..12
const MINUTE_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
// Snap an arbitrary minute count to the nearest allowed 5-minute option.
const snap5 = (m: number) => MINUTE_OPTIONS.reduce((a, b) => (Math.abs(b - m) < Math.abs(a - m) ? b : a), 0);

const FIELD =
  "w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2462b2]/20 focus:border-[#2462b2] transition";
const LABEL = "block text-xs font-semibold text-slate-600 mb-1.5";

export default function StartActivityModal({ isOpen, onClose, onCreated }: Props) {
  const { categories, fetchCategories } = useActivityCategoryStore();
  const {
    activities, activitiesUpdatedAt, pending, loading, fetchActivities, syncActivities,
    createActivity, enhanceWithAI, enhancing, saving, startActivity,
    stopActivity, changeStatus, deleteActivity,
  } = useActivityTaskStore();
  const { clients, fetchClients } = useClientStore();
  const { projects, fetchProjects } = useProjectStore();
  const { bids, fetchMyBids } = useBidStore();
  const { proposals, fetchProposals } = useProposalStore();

  const [view, setView] = useState<"list" | "form">("form");
  const [, setTick] = useState(0);

  // form fields
  const [categoryId, setCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [relatedLead, setRelatedLead] = useState("");
  const [relatedProject, setRelatedProject] = useState("");
  const [relatedJob, setRelatedJob] = useState("");
  const [relatedProposal, setRelatedProposal] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    fetchCategories(); fetchClients(); fetchProjects(); fetchMyBids(); fetchProposals();
    fetchActivities();
    syncActivities();
  }, [isOpen]);

  // Show the list first if activities already exist; else go straight to form.
  useEffect(() => {
    if (isOpen) setView(activities.length > 0 ? "list" : "form");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Live timers (1s) + ERP reconciliation (7s) while the list is visible.
  useEffect(() => {
    if (!isOpen || view !== "list") return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    const s = setInterval(() => syncActivities(), 3000);
    return () => { clearInterval(t); clearInterval(s); };
  }, [isOpen, view]);

  if (!isOpen) return null;

  // Local YYYY-MM-DD for "today" — used to block past due dates (only today or
  // upcoming dates are allowed when scheduling an activity/meeting).
  const todayStr = (() => {
    const d = new Date();
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
  })();
  const dueInPast = Boolean(dueDate) && dueDate < todayStr;

  const selectedCategory = categories.find((c) => c._id === categoryId);
  const estimatedMinutes =
    (parseInt(hours || "0") || 0) * 60 + (parseInt(minutes || "0") || 0);
  const formValid =
    Boolean(categoryId) && title.trim() && description.trim() &&
    Boolean(dueDate) && !dueInPast && estimatedMinutes > 0;

  const resetForm = () => {
    setCategoryId(""); setTitle(""); setDescription("");
    setHours(""); setMinutes(""); setDueDate("");
    setRelatedLead(""); setRelatedProject(""); setRelatedJob("");
    setRelatedProposal(""); setFiles([]);
  };

  const handleEnhance = async () => {
    if (!description.trim()) return;
    // Gather linked-record context so the AI names the real project/client/site.
    const leadObj: any = clients.find((c: any) => c._id === relatedLead);
    const projObj: any = projects.find((p: any) => p._id === relatedProject);
    const jobObj: any = bids.find((b: any) => b._id === relatedJob);
    const propObj: any = proposals.find((p: any) => p._id === relatedProposal);
    const context = {
      projectName: projObj?.name || "",
      clientName: leadObj?.name || "",
      website: leadObj?.website || "",
      jobTitle: jobObj?.jobTitle || "",
      proposalTitle: propObj?.title || "",
    };
    const res = await enhanceWithAI(description, selectedCategory?.name, context);
    if (res) {
      setTitle(res.title);
      if (res.enhancedDescription) setDescription(res.enhancedDescription);
      if (res.estimatedMinutes) {
        setHours(String(Math.floor(res.estimatedMinutes / 60)));
        setMinutes(String(snap5(res.estimatedMinutes % 60)));
      }
    }
  };

  const handleSubmit = async () => {
    if (!formValid) return;
    const fd = new FormData();
    fd.append("category", categoryId);
    fd.append("title", title.trim());
    fd.append("description", description.trim());
    fd.append("estimatedMinutes", String(estimatedMinutes));
    fd.append("dueDate", dueDate);
    if (relatedLead) fd.append("relatedLead", relatedLead);
    if (relatedProject) fd.append("relatedProject", relatedProject);
    if (relatedJob) fd.append("relatedJob", relatedJob);
    if (relatedProposal) fd.append("relatedProposal", relatedProposal);
    files.forEach((f) => fd.append("attachments", f));
    // Create only (status Planned) — timer is started later from the list.

    const created = await createActivity(fd);
    if (created) {
      resetForm();
      onCreated?.();
      setView("list"); // show the running activity in the list
    }
  };

  const priorityClass = (p: string) =>
    p === "High" ? "text-rose-600" : p === "Medium" ? "text-amber-600" : "text-slate-500";

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[92vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2.5">
            {view === "form" && activities.length > 0 && (
              <button
                onClick={() => setView("list")}
                className="p-1 -ml-1 text-slate-400 hover:text-slate-700 rounded-full"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <span className="w-8 h-8 rounded-full bg-blue-100 grid place-items-center text-[#2462b2]">
              <Play size={16} />
            </span>
            <h2 className="text-lg font-bold text-slate-800">Start Activity</h2>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-full transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* ===== LIST VIEW ===== */}
        {view === "list" ? (
          <>
            <div className="px-5 sm:px-6 py-3 border-b border-slate-100 flex items-center justify-between shrink-0">
              <span className="text-sm text-slate-500">
                {activities.length} {activities.length === 1 ? "activity" : "activities"}
              </span>
              <button
                onClick={() => { resetForm(); setView("form"); }}
                className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#2462b2] text-white rounded-lg hover:bg-[#1e52a0] transition-colors text-sm font-semibold shadow-sm"
              >
                <Plus size={16} /> Add Activity
              </button>
            </div>

            <div className="cre-scroll overflow-y-auto px-5 sm:px-6 py-4 space-y-2.5">
              {loading && activities.length === 0 ? (
                <div className="flex items-center justify-center gap-2 py-12 text-slate-500">
                  <Loader2 size={16} className="animate-spin text-[#2462b2]" /> Loading...
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-12 text-slate-400">No activities yet.</div>
              ) : (
                activities.map((a) => {
                  const cat = typeof a.category === "object" ? a.category : null;
                  const running = Boolean(a.runningSince);
                  const completed = a.status === "Completed";
                  return (
                    <div key={a._id} className="border border-slate-200 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {cat && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: cat.color || "#2462b2" }}>
                              {cat.name}
                            </span>
                          )}
                          <span className={`text-[11px] font-semibold flex items-center gap-1 ${priorityClass(a.priority)}`}>
                            <Flag size={11} /> {a.priority}
                          </span>
                        </div>
                        <h3 className="font-semibold text-slate-800 text-sm mt-1 truncate">
                          {a.title || a.description || "Untitled activity"}
                        </h3>
                      </div>

                      <div className="flex items-center gap-2 font-mono font-bold text-slate-700">
                        <Clock size={15} className={running ? "text-amber-500 animate-pulse" : "text-slate-400"} />
                        {fmtDuration(displayElapsed(a, activitiesUpdatedAt))}
                      </div>

                      <div className="flex items-center gap-2">
                        <select
                          className="px-2 py-1.5 text-xs border border-slate-300 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2462b2]/20"
                          value={a.status}
                          onChange={(e) => changeStatus(a._id, e.target.value as ActivityStatus)}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {!completed && (running ? (
                          <button onClick={() => stopActivity(a._id)} disabled={pending[a._id]} className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed">
                            {pending[a._id] ? <Loader2 size={13} className="animate-spin" /> : <Square size={13} />} Stop
                          </button>
                        ) : (
                          <button onClick={() => startActivity(a._id)} disabled={pending[a._id]} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#2462b2] hover:bg-[#1e52a0] text-white rounded-lg text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed">
                            {pending[a._id] ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} fill="currentColor" />} Start
                          </button>
                        ))}
                        <button onClick={() => { if (confirm("Delete this activity?")) deleteActivity(a._id); }} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-slate-100 rounded-lg">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        ) : (
          /* ===== FORM VIEW ===== */
          <>
            <div className="cre-scroll overflow-y-auto px-5 sm:px-6 py-5 space-y-5">
              {/* Category | Estimated | Due */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className={LABEL}>Activity Category <span className="text-rose-500">*</span></label>
                  <select className={FIELD} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                    <option value="">Select a category</option>
                    {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Estimated Time <span className="text-rose-500">*</span></label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <select value={hours} onChange={(e) => setHours(e.target.value)} className={`${FIELD} pr-2`}>
                        <option value="">Hrs</option>
                        {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h} h</option>)}
                      </select>
                    </div>
                    <div className="relative flex-1">
                      <select value={minutes} onChange={(e) => setMinutes(e.target.value)} className={`${FIELD} pr-2`}>
                        <option value="">Min</option>
                        {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m} m</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <label className={LABEL}>Due Date <span className="text-rose-500">*</span></label>
                  <input type="date" min={todayStr} className={FIELD} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                  {dueInPast && (
                    <p className="text-[11px] text-rose-500 mt-1">Pick today or an upcoming date.</p>
                  )}
                </div>
              </div>

              {/* Title — AI-generated via Enhance, but always editable so an AI
                  outage can never block a BD from creating a task. */}
              <div>
                <label className={LABEL}>Activity Title <span className="text-rose-500">*</span> <span className="font-normal text-slate-400">(AI-generated — or type your own)</span></label>
                <div className="relative">
                  <input
                    className={`${FIELD} pr-11 text-slate-700`}
                    placeholder="Type a title, or write a description and click Enhance with AI"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    title="Generated by AI from your description — you can edit it"
                  />
                  <button
                    onClick={handleEnhance}
                    disabled={enhancing || !description.trim()}
                    title="Generate title from description"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#2462b2] hover:bg-blue-100 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {enhancing ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
                  </button>
                </div>
                {!title && (
                  <p className="text-[11px] text-slate-400 mt-1">Fill the description and click “Enhance with AI” to generate the title — or type it yourself.</p>
                )}
              </div>

              {/* Description + Enhance with AI */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-600">Description <span className="text-rose-500">*</span></label>
                  <button
                    onClick={handleEnhance}
                    disabled={enhancing || !description.trim()}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#2462b2] hover:text-[#1e52a0] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {enhancing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                    Enhance with AI
                  </button>
                </div>
                <textarea
                  className={`${FIELD} resize-none`}
                  rows={3}
                  placeholder="Define the core objectives of this activity..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              {/* Relationships (optional) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <select className={FIELD} value={relatedLead} onChange={(e) => setRelatedLead(e.target.value)}>
                  <option value="">Related Lead</option>
                  {clients.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
                <select className={FIELD} value={relatedProject} onChange={(e) => setRelatedProject(e.target.value)}>
                  <option value="">Related Project</option>
                  {projects.map((p: any) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
                <select className={FIELD} value={relatedJob} onChange={(e) => setRelatedJob(e.target.value)}>
                  <option value="">Related Job</option>
                  {bids.map((b) => <option key={b._id} value={b._id}>{b.jobTitle || "Untitled job"}</option>)}
                </select>
                <select className={FIELD} value={relatedProposal} onChange={(e) => setRelatedProposal(e.target.value)}>
                  <option value="">Related Proposal</option>
                  {proposals.map((p) => <option key={p._id} value={p._id}>{p.title}</option>)}
                </select>
              </div>

              {/* Attachments */}
              <div>
                <label className={LABEL}>Attachments</label>
                <label className="border-2 border-dashed border-slate-200 rounded-xl p-6 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-[#2462b2]/40 hover:bg-blue-50/30 transition">
                  <UploadCloud className="text-slate-400" size={26} />
                  <span className="text-sm text-slate-500">Click to upload or drag and drop</span>
                  <span className="text-xs text-slate-400">PDF, DOCX, JPG (Max 10MB)</span>
                  <input type="file" className="hidden" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                </label>
                {files.length > 0 && (
                  <ul className="mt-2 text-xs text-slate-600 space-y-1">
                    {files.map((f, i) => <li key={i} className="truncate">• {f.name}</li>)}
                  </ul>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-5 sm:px-6 py-4 border-t border-slate-100 bg-slate-50/60 shrink-0">
              <button
                onClick={() => (activities.length > 0 ? setView("list") : onClose())}
                className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !formValid}
                className="inline-flex items-center gap-2 px-5 py-2 bg-[#2462b2] text-white rounded-lg hover:bg-[#1e52a0] transition-colors text-sm font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                Create Activity
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
